import { defineComponent as _defineComponent } from 'vue'
import { unref as _unref, openBlock as _openBlock, createBlock as _createBlock, createCommentVNode as _createCommentVNode, toDisplayString as _toDisplayString, createElementVNode as _createElementVNode, normalizeClass as _normalizeClass, normalizeStyle as _normalizeStyle, withCtx as _withCtx, createVNode as _createVNode, renderList as _renderList, Fragment as _Fragment, createElementBlock as _createElementBlock, resolveDynamicComponent as _resolveDynamicComponent, pushScopeId as _pushScopeId, popScopeId as _popScopeId } from "vue"

const _withScopeId = n => (_pushScopeId("data-v-cbd71658"),n=n(),_popScopeId(),n)
const _hoisted_1 = {
  class: "mx-auto",
  style: {"text-align":"center","display":"flex","align-items":"center","gap":"8px","justify-content":"center"}
}
const _hoisted_2 = { class: "mb-4" }
const _hoisted_3 = /*#__PURE__*/ _withScopeId(() => /*#__PURE__*/_createElementVNode("h3", { class: "text-lg font-medium mb-2" }, "Select Format", -1))
const _hoisted_4 = { class: "flex gap-3" }
const _hoisted_5 = ["onClick"]
const _hoisted_6 = { class: "flex items-center gap-2" }
const _hoisted_7 = { class: "mb-4" }
const _hoisted_8 = /*#__PURE__*/ _withScopeId(() => /*#__PURE__*/_createElementVNode("h3", { class: "text-xl font-semibold mb-2" }, "Company Overview", -1))
const _hoisted_9 = { class: "flex items-center justify-between" }
const _hoisted_10 = /*#__PURE__*/ _withScopeId(() => /*#__PURE__*/_createElementVNode("div", null, [
  /*#__PURE__*/_createElementVNode("h4", { class: "font-medium" }, "Complete Overview"),
  /*#__PURE__*/_createElementVNode("p", { class: "text-sm text-gray-600" }, "All enablers and criteria")
], -1))
const _hoisted_11 = /*#__PURE__*/ _withScopeId(() => /*#__PURE__*/_createElementVNode("h3", { class: "text-xl font-semibold mb-2" }, "Individual Enabler Reports", -1))
const _hoisted_12 = { class: "grid grid-cols-2 gap-4" }
const _hoisted_13 = ["onClick"]
const _hoisted_14 = { class: "flex items-center justify-between" }
const _hoisted_15 = { class: "font-medium" }
const _hoisted_16 = { class: "text-sm text-gray-600" }

import { ref, computed, reactive } from 'vue';
import { useRoute } from 'vue-router';
import axios from 'axios';
import { Application } from '@/data/Application';
import Dialog from 'primevue/dialog';
import Button from 'primevue/button';
import Toast from 'primevue/toast';
import { useToast } from 'primevue/usetoast';
import YBLoadingPopup from '../common/YBLoadingPopup.vue';
import {
	DownloadIcon,
	FileTextIcon,
	FileIcon,
	FileCodeIcon,
	Loader2Icon
} from 'lucide-vue-next';

type LoadingStates = {
	overview: boolean;
	[key: string]: boolean; // This allows any string key
};

interface Props {
	dark?: boolean;
	hover?: boolean;
	disabled?: boolean;
	btnStyle?: string;
	modelValue?: boolean;
}


export default /*#__PURE__*/_defineComponent({
  __name: 'DownloadReport',
  props: {
    dark: { type: Boolean, default: false },
    hover: { type: Boolean, default: false },
    disabled: { type: Boolean, default: false },
    btnStyle: { default: '' },
    modelValue: { type: Boolean, default: false }
  },
  emits: ["update:modelValue", "buttonClick", "hover"],
  setup(__props: any, { emit: __emit }) {

const route = useRoute();
const toast = useToast();
const showModal = ref(false);
const isLoading = ref(false);
const selectedFormat = ref('pdf');
const showLoadingPopup = ref(false);
const popupKey = ref(0);



const loadingStates = reactive<LoadingStates>({
	overview: false,
	NC: false,
	OG: false,
	OA: false,
	TII: false,
	HII: false,
	OC: false,
	HKS: false,
	SC: false
});

const props = __props;

// Emits
const emit = __emit;

const isDark = computed(() => props.dark);
const isHovered = computed(() => props.hover);

// Event handlers
const handleClick = () => {
	emit('buttonClick');
	openModal();
};

const handleHover = (value: boolean) => {
	emit('hover', value);
};

const openModal = () => {
	showModal.value = true;
	emit('update:modelValue', true);
};

// Structure data
const enablerSections = [
	{
		name: "Network Characteristics",
		code: "NETWORK_CHARACTERISTICS",
		criteria: [
			{ name: "Relationships based on trust", code: "C1" },
			{ name: "Performance metrics, measurement and benchmarking", code: "C2" },
			{ name: "Process integration and collaborative work", code: "C3" },
			{ name: "Knowledge sharing", code: "C4" },
			{ name: "Quick connect and disconnect", code: "C5" }
		]
	},
	{
		name: "Organisational Governance",
		code: "ORGANISATIONAL_GOVERNANCE",
		criteria: [
			{ name: "Empowerment & rewards", code: "C6" },
			{ name: "Performance measures", code: "C7" },
			{ name: "Flexible Budgeting", code: "C8" }
		]
	},
	{
		name: "Organisational Architecture",
		code: "ORGANISATIONAL_ARCHITECTURE",
		criteria: [
			{ name: "Standardisation & simplification", code: "C9" },
			{ name: "Modularity", code: "C10" },
			{ name: "Integration", code: "C11" }
		]
	},
	{
		name: "Technical IT Infrastructure",
		code: "TECHNICAL_IT_INFRASTRUCTURE",
		criteria: [
			{ name: "IT connectivity", code: "C12" },
			{ name: "IT compatibility", code: "C13" },
			{ name: "Application functionality", code: "C14" }
		]
	},
	{
		name: "Human IT Infrastructure",
		code: "HUMAN_IT_INFRASTRUCTURE",
		criteria: [
			{ name: "Business understanding", code: "C15" },
			{ name: "Technical knowledge", code: "C16" },
			{ name: "Management skills", code: "C17" },
			{ name: "Technical skills", code: "C18" }
		]
	},
	{
		name: "Organisational Culture",
		code: "ORGANISATIONAL_CULTURE",
		criteria: [
			{ name: "Leadership by coaching", code: "C19" },
			{ name: "Fostering a culture of knowledge sharing & learning", code: "C20" },
			{ name: "Interactions among employees", code: "C21" }
		]
	},
	{
		name: "Human Knowledge & Skills",
		code: "HUMAN_KNOWLEDGE_&_SKILLS",
		criteria: [
			{ name: "Knowledge (tacit), skills variety and redeployability", code: "C22" },
			{ name: "Speed of acquiring and developing new skills", code: "C23" }
		]
	},
	{
		name: "SRL Capabilities",
		code: "SRL_CAPABILITIES",
		criteria: [{
			name: "Sensing Capabilities Measurement", code: "C24"
		},
		{
			name: "Responding Capabilities Measurement", code: "C25"
		},
		{
			name: "Learning Capabilities Measurement", code: "C26"
		}]
	}
];

const availableFormats = [
	{ label: 'PDF', ext: '.pdf', value: 'pdf', icon: FileTextIcon },
	{ label: 'Markdown', ext: '.md', value: 'markdown', icon: FileCodeIcon },
	{ label: 'Word', ext: '.docx', value: 'word', icon: FileIcon }
];

const downloadFile = async (data: any, filename: string): Promise<void> => {
	return new Promise((resolve) => {
		const blob = data instanceof Blob ? data : new Blob([data]);
		const url = window.URL.createObjectURL(blob);
		const link = document.createElement('a');

		// Listen for the download completion
		link.addEventListener('click', () => {
			setTimeout(() => {
				window.URL.revokeObjectURL(url);
				resolve();
			}, 100);
		});

		link.href = url;
		link.setAttribute('download', filename);
		document.body.appendChild(link);
		link.click();
		link.remove();
	});
};

const downloadCompanyOverview = async () => {
	if (loadingStates.overview) return;

	try {
		loadingStates.overview = true;
		popupKey.value++;
		showLoadingPopup.value = true;

		const response = await axios.get(
			`${Application.instance.apiEndPoint}/reports/company/${route.params.qid}`,
			{
				params: {
					format: selectedFormat.value,
					refresh: false
				},
				responseType: selectedFormat.value === 'markdown' ? 'text' : 'blob'
			}
		);

		await downloadFile(response.data, `company-overview${availableFormats.find(x => x.value === selectedFormat.value)?.ext ?? '.txt'}`);

		showLoadingPopup.value = false;
		toast.add({
			severity: 'success',
			summary: 'Success',
			detail: 'Report downloaded successfully',
			life: 3000
		});

	} catch (error: any) {
		console.error('Error downloading company overview:', error);
		showLoadingPopup.value = false;

		// Handle blob error responses
		if (error.response?.data instanceof Blob) {
			try {
				const errorText = await error.response.data.text();
				const errorMessage = errorText.startsWith('{') ? JSON.parse(errorText) : errorText;
				toast.add({
					severity: 'error',
					summary: 'Download Failed',
					detail: typeof errorMessage === 'string' ? errorMessage : errorMessage.message || 'Failed to download report',
					life: 5000
				});
				return;
			} catch (blobError) {
				console.error('Error parsing blob error:', blobError);
			}
		}

		// Fallback error handling
		toast.add({
			severity: 'error',
			summary: 'Download Failed',
			detail: error.message || error.response?.statusText || 'Failed to download report',
			life: 5000
		});
	} finally {
		loadingStates.overview = false;
	}
};

const downloadEnablerReport = async (enablerCode: keyof LoadingStates) => {
	if (loadingStates[enablerCode]) return;

	try {
		loadingStates[enablerCode] = true;
		popupKey.value++;
		showLoadingPopup.value = true;

		const response = await axios.get(
			`${Application.instance.apiEndPoint}/reports/enabler/${route.params.qid}/${enablerCode}`,
			{
				params: {
					format: selectedFormat.value,
					refresh: false
				},
				responseType: selectedFormat.value === 'markdown' ? 'text' : 'blob'
			}
		);

		await downloadFile(response.data, `${enablerCode}-report${availableFormats.find(x => x.value === selectedFormat.value)?.ext ?? '.txt'}`);
		showLoadingPopup.value = false;

		toast.add({
			severity: 'success',
			summary: 'Success',
			detail: 'Report downloaded successfully',
			life: 3000
		});
	} catch (error: any) {
		console.error('Error downloading enabler report:', error);
		showLoadingPopup.value = false;

		// Handle blob error responses
		if (error.response?.data instanceof Blob) {
			try {
				const errorText = await error.response.data.text();
				const errorMessage = errorText.startsWith('{') ? JSON.parse(errorText) : errorText;
				toast.add({
					severity: 'error',
					summary: 'Download Failed',
					detail: typeof errorMessage === 'string' ? errorMessage : errorMessage.message || 'Failed to download report',
					life: 5000
				});
				return;
			} catch (blobError) {
				console.error('Error parsing blob error:', blobError);
			}
		}

		// Fallback error handling
		toast.add({
			severity: 'error',
			summary: 'Download Failed',
			detail: error.message || error.response?.statusText || 'Failed to download report',
			life: 5000
		});
	} finally {
		loadingStates[enablerCode] = false;
	}
};

const handleClose = () => {
	showLoadingPopup.value = false;
};

return (_ctx: any,_cache: any) => {
  return (_openBlock(), _createElementBlock("div", null, [
    _createVNode(_unref(Button), {
      onClick: handleClick,
      disabled: _ctx.disabled || isLoading.value,
      class: _normalizeClass(["button-default header-button ici_button", [!isHovered.value ? (isDark.value ? 'button-normal-dark' : 'button-normal') : 'button-over']]),
      style: _normalizeStyle(_ctx.btnStyle),
      onMouseenter: _cache[0] || (_cache[0] = ($event: any) => (handleHover(true))),
      onMouseleave: _cache[1] || (_cache[1] = ($event: any) => (handleHover(false)))
    }, {
      default: _withCtx(() => [
        _createElementVNode("div", _hoisted_1, [
          (isLoading.value)
            ? (_openBlock(), _createBlock(_unref(Loader2Icon), {
                key: 0,
                class: "animate-spin"
              }))
            : (_openBlock(), _createBlock(_unref(DownloadIcon), { key: 1 })),
          _createElementVNode("label", null, _toDisplayString(isLoading.value ? 'Preparing Download...' : 'Download Report'), 1)
        ])
      ]),
      _: 1
    }, 8, ["disabled", "class", "style"]),
    _createVNode(_unref(Dialog), {
      visible: showModal.value,
      "onUpdate:visible": _cache[2] || (_cache[2] = ($event: any) => ((showModal).value = $event)),
      modal: "",
      header: "Download Reports",
      style: { width: '50vw' },
      class: "download-modal"
    }, {
      default: _withCtx(() => [
        _createElementVNode("div", _hoisted_2, [
          _hoisted_3,
          _createElementVNode("div", _hoisted_4, [
            (_openBlock(), _createElementBlock(_Fragment, null, _renderList(availableFormats, (format) => {
              return _createElementVNode("div", {
                key: format.value,
                onClick: ($event: any) => (selectedFormat.value = format.value),
                class: _normalizeClass([
							'cursor-pointer p-3 rounded-lg border transition-colors',
							selectedFormat.value === format.value ? 'border-blue-500 bg-blue-50' : 'border-gray-200 hover:bg-gray-50'
						])
              }, [
                _createElementVNode("div", _hoisted_6, [
                  (_openBlock(), _createBlock(_resolveDynamicComponent(format.icon), { class: "w-5 h-5" })),
                  _createElementVNode("span", null, _toDisplayString(format.label), 1)
                ])
              ], 10, _hoisted_5)
            }), 64))
          ])
        ]),
        _createElementVNode("div", _hoisted_7, [
          _hoisted_8,
          _createElementVNode("div", {
            class: _normalizeClass(["p-3 border rounded-lg transition-colors relative hover:bg-gray-50 cursor-pointer", [loadingStates.overview ? 'bg-gray-100' : '']]),
            onClick: downloadCompanyOverview
          }, [
            _createElementVNode("div", _hoisted_9, [
              _hoisted_10,
              (loadingStates.overview)
                ? (_openBlock(), _createBlock(_unref(Loader2Icon), {
                    key: 0,
                    class: "animate-spin text-gray-500"
                  }))
                : (_openBlock(), _createBlock(_unref(DownloadIcon), {
                    key: 1,
                    class: "text-gray-500"
                  }))
            ])
          ], 2)
        ]),
        _createElementVNode("div", null, [
          _hoisted_11,
          _createElementVNode("div", _hoisted_12, [
            (_openBlock(), _createElementBlock(_Fragment, null, _renderList(enablerSections, (section) => {
              return _createElementVNode("div", {
                key: section.code,
                class: _normalizeClass(["p-3 border rounded-lg transition-colors relative", [loadingStates[section.code] ? 'bg-gray-100' : 'hover:bg-gray-50 cursor-pointer']]),
                onClick: ($event: any) => (downloadEnablerReport(section.code))
              }, [
                _createElementVNode("div", _hoisted_14, [
                  _createElementVNode("div", null, [
                    _createElementVNode("h4", _hoisted_15, _toDisplayString(section.name), 1),
                    _createElementVNode("p", _hoisted_16, _toDisplayString(section.criteria?.length || 0) + " criteria", 1)
                  ]),
                  (loadingStates[section.code])
                    ? (_openBlock(), _createBlock(_unref(Loader2Icon), {
                        key: 0,
                        class: "animate-spin text-gray-500"
                      }))
                    : (_openBlock(), _createBlock(_unref(DownloadIcon), {
                        key: 1,
                        class: "text-gray-500"
                      }))
                ])
              ], 10, _hoisted_13)
            }), 64))
          ])
        ]),
        (showLoadingPopup.value)
          ? (_openBlock(), _createBlock(YBLoadingPopup, {
              initialVisible: true,
              onClose: handleClose,
              key: popupKey.value
            }))
          : _createCommentVNode("", true),
        _createVNode(_unref(Toast), { position: "bottom-right" })
      ]),
      _: 1
    }, 8, ["visible"])
  ]))
}
}

})